import { CSSProperties, memo, Ref } from "react";

import { ShiftSelectionAction } from "components/BulkActions/useShiftSelectionManager";

import TableBodyRow from "../Table/Row";
import {
  StickyRightPredicate,
  TableCellPerColumn,
  TableSimpleDataItem,
  TableSimpleSelectionStore,
} from "./types";
import { isStickyLeft, isStickyRight } from "./helpers";
import { TableColumnMeta } from "../Table/types";

type TableSimpleRowProps<T extends TableSimpleDataItem> = {
  item: T;
  itemId: string;
  columns: TableColumnMeta[];
  isFirstColumnPinned: boolean;
  selectionStore?: TableSimpleSelectionStore;
  hasMoreSelectionOptions?: boolean;
  shiftSelectionAction?: ShiftSelectionAction;
  cellPerColumn: TableCellPerColumn<T>;
  keyboardProps: Record<string, unknown>;
  measureRef?: Ref<HTMLDivElement>;
  dataIndex?: number;
  virtualized?: boolean;
  className?: string;
  stickyRight?: StickyRightPredicate;
  style?: CSSProperties;
  ariaLevel?: number;
  ariaExpanded?: boolean;
  isActive?: boolean;
};

const TableSimpleRow = <T extends TableSimpleDataItem>({
  item,
  itemId,
  columns,
  isFirstColumnPinned,
  selectionStore,
  hasMoreSelectionOptions,
  shiftSelectionAction,
  cellPerColumn,
  keyboardProps,
  measureRef,
  dataIndex,
  virtualized,
  className,
  stickyRight = isStickyRight,
  style,
  ariaLevel,
  ariaExpanded,
  isActive,
}: TableSimpleRowProps<T>) => {
  return (
    <TableBodyRow
      {...keyboardProps}
      ref={measureRef}
      dataIndex={dataIndex}
      virtualized={virtualized}
      className={className}
      isActive={isActive}
      style={style}
      aria-level={ariaLevel}
      aria-expanded={ariaExpanded}
    >
      {columns.map((column, i) => {
        const cellId = `${itemId}-${column.id}`;
        const Cell = cellPerColumn[column.id];
        const stickyLeft = isStickyLeft(i, isFirstColumnPinned);
        const withSelection = i === 0 && selectionStore && itemId;

        return (
          <Cell
            key={cellId}
            id={cellId}
            stickyLeft={stickyLeft}
            stickyRight={stickyRight(i, columns.length)}
            item={item}
            isFirst={i === 0}
            {...(withSelection && {
              selectionId: itemId,
              selectionStore,
              hasMoreSelectionOptions,
              shiftSelectionAction,
            })}
          />
        );
      })}
    </TableBodyRow>
  );
};

TableSimpleRow.displayName = "DS.TableSimple.Row";

export default memo(TableSimpleRow) as typeof TableSimpleRow;
